import { useMemo } from 'react'
import { useConfig } from '@peracto/peracto-config'
import algoliasearch from 'algoliasearch/lite'
import isEmpty from 'lodash/isEmpty'

// Attributes that don't start with 'attributes.*'
const UNPREFIXED_ATTRIBUTES = ['sku']

const toFacetFilters = (attributes, attributeOperator) => {
    if (!(attributes?.length > 0)) {
        return undefined
    }

    const filters = attributes.reduce((acc, { attribute, value }) => {
        const attributeKey = UNPREFIXED_ATTRIBUTES.includes(attribute)
            ? attribute
            : `attributes.${attribute}`
        if (value.length > 0) acc.push(`${attributeKey}:${value}`)

        return acc
    }, [])

    return attributeOperator === 'and' ? filters : [filters]
}

/**
 * Search setup shared by the product blocks. Everything handed to <InstantSearch> and <Configure>
 * is stable between renders: react-instantsearch 7 re-applies search parameters whenever they
 * change, so a new search client or freshly derived filters on every render keeps it re-rendering.
 */
export const useProductSearch = ({ attributes, attributeOperator, numberOfProducts }) => {
    const config = useConfig()
    const algolia = config.get('algolia') || {}
    const { applicationId, searchApiKey, indexName } = algolia

    const searchClient = useMemo(
        () => (applicationId && searchApiKey ? algoliasearch(applicationId, searchApiKey) : null),
        [applicationId, searchApiKey]
    )

    const attributesKey = JSON.stringify(attributes || [])
    const facetFilters = useMemo(() => toFacetFilters(attributes, attributeOperator), [
        attributesKey,
        attributeOperator,
    ])

    const hitsPerPage = Number(numberOfProducts) || 4

    const configureProps = useMemo(() => ({ hitsPerPage, ...(facetFilters && { facetFilters }) }), [
        hitsPerPage,
        facetFilters,
    ])

    return {
        hasConfig: !isEmpty(algolia) && !!searchClient,
        searchClient,
        indexName,
        configureProps,
    }
}
